import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from '../hooks/useAuth';
import { useDownloadVersion } from '../hooks/useDownloadVersion';
import { checkoutApi } from '../api/checkout';
import { pricingApi } from '../api/pricing';
import { detectCurrency, formatFromTable } from '../lib/currency';
import { trackEvent, trackBeginCheckout } from '../lib/analytics';
import { getHeroVariant, isPaidClick, HERO_EXPERIMENT, BUY } from '../lib/experiment';
import { useIsHandheld } from '../hooks/useIsHandheld';
import LeadCapturePanel from './LeadCapturePanel';
import ShotsCarousel from './ShotsCarousel';
import { HOME_SHOTS } from '../lib/shots';

// WebP for the page, PNG left untouched at its own URL: /hero-plugin.png is the
// <g:image_link> in all 17 Merchant Center feeds and the og:image of the 48 docs
// articles. Re-pointing either would send every item back for a 3-day review and
// change what social scrapers fetch. The browser download drops 1.0 MB -> 64 KB.
const HERO_PLUGIN_IMAGE = "/hero-plugin-screen.webp";

// One definition, rendered above the paid card or below it depending on the
// visit. Duplicating the markup for the two orders is how two copies drift.
function TrialCard({ t, trialDisplay }) {
  return (
    <div className="price">
      <h3>{t("pricing.trial.name")}</h3>
      <div className="num">{trialDisplay}<small>{t("pricing.trial.period")}</small></div>
      <ul>
        <li>{t("pricing.trial.feature1")}</li>
        <li>{t("pricing.trial.feature2")}</li>
        <li>{t("pricing.trial.feature3")}</li>
        <li>{t("pricing.trial.feature4")}</li>
        <li>{t("pricing.trial.feature5")}</li>
      </ul>
      <a href="/download" className="btn">{t("pricing.trial.cta")}</a>
    </div>
  );
}

const TrapManLanding = () => {
  const { t, i18n } = useTranslation("landing");
  const { user } = useAuth();
  const location = useLocation();
  const versionInfo = useDownloadVersion();

  const isHandheld = useIsHandheld();
  // Which CTA opened the capture panel: null | "landing_hero" | "landing_pricing"
  const [capture, setCapture] = useState(null);

  const params = new URLSearchParams(location.search);

  // The opt-in link in the resume email lands on /?optin=ok after the backend
  // has written the consent proof. This only reports it.
  const optinConfirmed = params.get("optin") === "ok";

  // A Google Shopping click already showed the price in the ad, so it arrives
  // wanting to buy, not to evaluate: the hero leads with Buy and the trial drops
  // to a quiet link underneath.
  //
  // The tell is `gclid`, which Google's auto-tagging appends to every paid
  // click. Deliberately not a feed change: the URL in the Merchant Center feed
  // stays byte-for-byte what it is today, so nothing goes back for the 3-day
  // item review, and the free listings that earn the organic clicks are
  // untouched. The Merchant Center crawler arrives without a gclid and sees the
  // ordinary page at the same price, which is what it checks. `src=gshop` is
  // the same variant without spending a click, for checking the page by hand.
  // isPaidClick rather than the test inline: this predicate and the
  // experiment's exclusion have to be the same one, or a visitor could get the
  // buy-first hero and still be counted in an arm. See lib/experiment.js.
  const shoppingIntent = isPaidClick(params);

  // The A/B test (lib/experiment.js). Assigned in a state initialiser so the
  // roll happens once per mount and never mid-render: a variant that changed
  // between two renders would swap the hero under the visitor's cursor.
  //
  // Null for a Google click, which shoppingIntent already forces into the
  // buy-first hero — see isExcluded in experiment.js.
  const [heroVariant] = useState(() => getHeroVariant());

  // Both roads to the buy-first hero. They are not the same page: the Shopping
  // click was quoted the price in the ad and gets the trial as a quiet link,
  // while the experiment's variant keeps it as a real button under Buy. The
  // trial is not decoration — it precedes 5 of our 11 paid orders — so the
  // test moves it down the page rather than off it.
  const buyFirst = shoppingIntent || heroVariant === BUY;

  // One exposure per mount, so the denominator of the test is "sessions that
  // actually saw a hero" rather than every session that touched the site.
  // A ref, not trackEventOnce: that one is permanent per browser, and this has
  // to fire again on a later visit.
  const exposureRef = useRef(false);
  useEffect(() => {
    if (!heroVariant || exposureRef.current) return;
    exposureRef.current = true;
    trackEvent("experiment_exposed", { experiment: HERO_EXPERIMENT, variant: heroVariant });
  }, [heroVariant]);
  // Ref guard, not trackEventOnce: StrictMode double-invokes this effect in
  // development, but lead_optin_confirmed must still fire once per real page load.
  const optinReportedRef = useRef(false);
  useEffect(() => {
    if (!optinConfirmed || optinReportedRef.current) return;
    optinReportedRef.current = true;
    trackEvent("lead_optin_confirmed");
  }, [optinConfirmed]);

  const [prices, setPrices] = useState([]);
  // Display currency follows the UI language (fr/es/de/it -> EUR, en -> browser
  // region), for the marketing page only. The actual charge currency is handled
  // by Stripe Adaptive Pricing at checkout (the session sets no currency).
  useEffect(() => {
    pricingApi.getPricing().then((resp) => {
      const rows = Array.isArray(resp.data.prices) ? resp.data.prices : [];
      setPrices(rows);
    }).catch(() => {});
  }, []);

  // Scroll behaviour for the landing. Now that the top bar is a persistent shell
  // and its links route client-side, arriving here (e.g. /#pricing from the nav
  // or a docs CTA) no longer triggers a full reload — so we drive the scroll
  // ourselves on every in-app navigation: jump to the hash target once it's in
  // the DOM, or back to the top when arriving without a hash. The window-load
  // retry covers late-loading images (hero, kit) on a cold/deep-link load.
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) { window.scrollTo(0, 0); return; }
    const scrollToHash = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    };
    scrollToHash();
    window.addEventListener("load", scrollToHash);
    return () => window.removeEventListener("load", scrollToHash);
  }, [location.pathname, location.hash]);

  const currency = detectCurrency(
    i18n.language,
    prices.map((r) => r.currency),
    params.get("cur"),
  );
  const priceRow = prices.length ? formatFromTable(prices, currency) : null;
  const priceDisplay = priceRow ? priceRow.display : (currency === 'eur' ? '59 €' : '$59');
  // Both present only while a launch offer runs; null otherwise, which is what
  // makes every render below fall back to the plain price with no branching.
  const originalDisplay = priceRow ? priceRow.original_display : null;
  // Free-trial price mirrors the producer currency (e.g. "$59" -> "$0", "59 €" -> "0 €").
  const trialDisplay = priceDisplay.replace(/[\d.,]+/, '0');

  // Buy goes to Stripe, from any screen, signed in or not.
  //
  // It used to fork three ways: a phone visitor got the email-capture panel, a
  // signed-out desktop visitor was sent to /signup, and only a signed-in one
  // reached a payment form. That existed because buying meant creating an
  // account, verifying the address and logging in first — five steps, which is
  // why 24 mobile taps over 30 days produced no purchases at all. Guest
  // checkout removed those steps, so the detours have nothing left to protect
  // against: someone who taps Buy is taken to a payment form.
  //
  // A signed-in buyer still gets the account-bound session, so the order lands
  // on the account they already have.
  const handleBuy = async (e) => {
    e.preventDefault();
    trackBeginCheckout({
      step: user ? "stripe_session" : "stripe_guest",
      page: "landing",
    });
    try {
      const resp = user
        ? await checkoutApi.createSession()
        : await checkoutApi.createGuestSession();
      window.location.href = resp.data.url;
    } catch {
      // Stripe unreachable. A signed-in visitor has a dashboard to fall back
      // to; a signed-out one has nothing there, so send them to signup, which
      // is at least a way to buy.
      window.location.href = user ? "/dashboard" : "/signup";
    }
  };

  return (
  <>
    <header className="hero" id="plugin">
      {optinConfirmed && (
        <p className="pill" style={{ marginBottom: 16 }}>{t("optinConfirmed")}</p>
      )}
      <div className="hero-grid">
        <div>
          <img src="/trapman-logo.webp" alt="TrapMan" className="product-mark" />
          <span className="eyebrow"><b>●</b> {versionInfo?.version ? `v${versionInfo.version}` : "v1.3.0"}</span>
          <h1 className="hero-h1">{t("hero.title1")}<br/>{t("hero.title2")} <em>{t("hero.titleEm")}</em></h1>
          {buyFirst ? (
            <>
              <div className={shoppingIntent ? "hero-ctas" : "hero-ctas hero-ctas-stack"}>
                <a href="/signup" onClick={handleBuy} className="btn btn-primary big-btn hero-buy-lg">
                  {/* The left hero column is ~285px, too narrow for this label
                      on one line in French, so it wraps — and it was breaking
                      between the number and the currency ("59" / "€").
                      One outer span because .btn is itself a flex container:
                      two children would be laid out as two flex items and end
                      up at opposite ends of the button rather than on two
                      lines. Inside it, ordinary text, with the price held
                      together so the break falls before it. */}
                  <span className="hero-buy-label">
                    {t("hero.ctaBuy")} <span className="hero-buy-price">— {originalDisplay ? <><s className="price-was">{originalDisplay}</s> {priceDisplay}</> : priceDisplay}</span>
                  </span>
                </a>
                {/* The experiment's variant keeps the trial as a real button,
                    stacked directly under Buy. The Shopping hero demotes it to
                    the quiet link further down instead: that visitor was quoted
                    a price in the ad and arrived to pay it. */}
                {!shoppingIntent && (
                  <a href="/download" className="btn big-btn">{t("hero.ctaTrial")}</a>
                )}
              </div>
              <div className="hero-trust">
                <span className="pill">{t("pricing.producer.feature1")}</span>
                <span className="pill">{t("pricing.producer.feature2")}</span>
                <span className="pill">{t("pricing.producer.feature3")}</span>
                <span className="pill">{t("pricing.producer.feature4")}</span>
              </div>
              {shoppingIntent && (
                <a href="/download" className="gshop-trial">{t("hero.ctaTrial")}</a>
              )}
            </>
          ) : (
            <div className="hero-ctas">
              <a href="/download" className="btn big-btn">{t("hero.ctaTrial")}</a>
              <a href="/signup" onClick={handleBuy} className="btn btn-primary big-btn">{t("hero.ctaBuy")}</a>
            </div>
          )}
          {isHandheld && !user && capture !== "landing_hero" && (
            /* Buy now goes to Stripe, so the email path needs its own door.
               Kept because it is where the homepage's `Lead` events come
               from, and phones are where a desktop plugin cannot be
               installed — the reason it was built. */
            <button type="button" onClick={() => setCapture("landing_hero")}
              className="link-quiet" style={{ display: "block", margin: "12px auto 0", background: "none", border: 0 }}>
              {t("hero.emailInstead")}
            </button>
          )}
          {capture === "landing_hero" && (
            <LeadCapturePanel source="landing_hero" autoReveal onDismiss={() => setCapture(null)} />
          )}
        </div>
        <div className="hero-art">
          <img src={HERO_PLUGIN_IMAGE} alt={t("hero.imageAlt")} className="hero-photo" />
        </div>
      </div>
      <p className="hero-lead">{t("hero.lead")}</p>
      {/* Every visitor gets the screenshots, whichever hero arm they drew: the
          hero_buy_first test now compares the order of Buy and the trial and
          nothing else. Until 2026-10-19 the carousel was part of the buy arm
          and the trial arm had the DAW tagline in its place. */}
      <ShotsCarousel shots={HOME_SHOTS} flat />
    </header>

    <section id="features">
      <div className="section-intro">
        <h2 className="h2">{t("features.title1")}<br/>{t("features.title2")}</h2>
        <p className="sub">{t("features.sub")}</p>
      </div>
      <div className="features">
        <div className="feat">
          <div className="ico">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="1" y="3" width="16" height="3" rx="1" fill="currentColor"/>
              <rect x="1" y="8" width="10" height="3" rx="1" fill="currentColor" opacity=".6"/>
              <rect x="1" y="13" width="13" height="3" rx="1" fill="currentColor" opacity=".3"/>
            </svg>
          </div>
          <h3>{t("features.item1.title")}</h3>
          <p>{t("features.item1.desc")}</p>
          <span className="feat-num">/ 01</span>
        </div>
        <div className="feat">
          <div className="ico">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" fill="none"/>
              <path d="M9 3v6l4 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </div>
          <h3>{t("features.item2.title")}</h3>
          <p>{t("features.item2.desc")}</p>
          <span className="feat-num">/ 02</span>
        </div>
        <div className="feat">
          <div className="ico">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="2" y="2" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" fill="none"/>
              <path d="M6 9h6M9 6v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </div>
          <h3>{t("features.item3.title")}</h3>
          <p>{t("features.item3.desc")}</p>
          <span className="feat-num">/ 03</span>
        </div>
      </div>
    </section>

    <section id="kits">
      <div className="kit">
        <div>
          <h2 className="h2" style={{ marginTop: 16 }}>{t("kits.title1")}<br/>{t("kits.title2")}</h2>
          <p className="sub">{t("kits.sub")}</p>
          <div style={{ marginTop: 28, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span className="pill">{t("kits.pill808s")}</span>
            <span className="pill">{t("kits.pillKicks")}</span>
            <span className="pill">{t("kits.pillHiHats")}</span>
            <span className="pill">{t("kits.pillSnares")}</span>
            <span className="pill">{t("kits.pillClaps")}</span>
            <span className="pill">{t("kits.pillPercussions")}</span>
          </div>
          {/* The shared library at /kits: the landing's only link into it
              besides the nav, and the one that explains what it is. */}
          <div className="kit-community">
            <h3>{t("kits.communityTitle")}</h3>
            <p>{t("kits.communitySub")}</p>
            <Link to="/kits" className="btn btn-primary big-btn">{t("kits.communityCta")}</Link>
          </div>
        </div>
        <div className="kit-feature">
          <img src="/core-kit.webp" alt={t("kits.imageAlt")} className="kit-cover" loading="lazy" decoding="async" />
        </div>
      </div>
    </section>

    <div className="marquee">
      <div className="marquee-inner">
        <span>{t("marquee.item1")}</span><em>●</em><span>{t("marquee.item2")}</span><em>●</em><span>{t("marquee.item3")}</span><em>●</em><span>{t("marquee.item4")}</span><em>●</em><span>{t("marquee.item5")}</span><em>●</em><span>{t("marquee.item6")}</span><em>●</em>
        <span>{t("marquee.item1")}</span><em>●</em><span>{t("marquee.item2")}</span><em>●</em><span>{t("marquee.item3")}</span><em>●</em><span>{t("marquee.item4")}</span><em>●</em><span>{t("marquee.item5")}</span><em>●</em><span>{t("marquee.item6")}</span><em>●</em>
      </div>
    </div>

    <section id="pricing">
      <div className="section-intro">
        <h2 className="h2">{t("pricing.title1")}<br/>{t("pricing.title2")}</h2>
        <p className="sub">{t("pricing.sub")}</p>
      </div>
      {/* A Shopping click was quoted one price in the ad and arrives to pay it, so
          it gets that card alone: the trial card next to it is a second offer
          nobody asked for, and the one thing that can still talk it out of the
          purchase. The ordinary page keeps both, which is also the page the
          Merchant Center crawler fetches — it arrives without a gclid. */}
      <div className={shoppingIntent ? "prices prices-solo" : "prices"}>
        {/* Side by side for an ordinary visitor. A paid click gets the same
            two cards stacked, buy first: the ad quoted a price, so the trial
            must not be the first thing they read — but hiding it outright
            removed the only zero-risk way in. */}
        {!shoppingIntent && <TrialCard t={t} trialDisplay={trialDisplay} />}
        <div className="price pop">
          <h3>{t("pricing.producer.name")}</h3>
          <div className="num">
            {originalDisplay && <s className="price-was">{originalDisplay}</s>}
            {priceDisplay}<small>{t("pricing.producer.period")}</small>
          </div>
          <ul>
            <li>{t("pricing.producer.feature1")}</li>
            <li>{t("pricing.producer.feature2")}</li>
            <li>{t("pricing.producer.feature3")}</li>
            <li>{t("pricing.producer.feature4")}</li>
            <li>{t("pricing.producer.feature5")}</li>
          </ul>
          <a href="/signup" onClick={handleBuy} className="btn btn-primary">{t("pricing.producer.cta")}</a>
          {isHandheld && !user && capture !== "landing_pricing" && (
            /* Buy now goes to Stripe, so the email path needs its own door.
               Kept because it is where the homepage's `Lead` events come
               from, and phones are where a desktop plugin cannot be
               installed — the reason it was built. */
            <button type="button" onClick={() => setCapture("landing_pricing")}
              className="link-quiet" style={{ display: "block", margin: "12px auto 0", background: "none", border: 0 }}>
              {t("hero.emailInstead")}
            </button>
          )}
          {capture === "landing_pricing" && (
            <LeadCapturePanel source="landing_pricing" autoReveal onDismiss={() => setCapture(null)} />
          )}
        </div>
        {shoppingIntent && <TrialCard t={t} trialDisplay={trialDisplay} />}
      </div>
    </section>

  </>
  );
};

export default TrapManLanding;
